import { useEffect, type ReactNode } from 'react';
import { DropdownMenu, DropdownMenuContent, DropdownMenuTrigger } from '@monksflow/monks-ui';
import type { ProjectListItem } from '@/entities/project';
import { useProjectPicker } from '@/shared/hooks';
import { ProjectPickerContent } from '../SaveToProjectPopover/ProjectPickerContent';
import { ProjectPickerSearch } from '../SaveToProjectPopover/ProjectPickerSearch';

/** The `useProjectPicker` return, surfaced to consumers via render props. */
type ProjectPicker = ReturnType<typeof useProjectPicker>;

interface ProjectPickerPopoverProps {
  /** Trigger element (rendered inside `DropdownMenuTrigger asChild`). */
  children: ReactNode;
  workspaceId: string | null | undefined;
  /**
   * Renders one project row. Receives the project and the live picker so a
   * consumer can drive selection/close behavior (single-select wrappers call
   * `picker.setOpen(false)`; multi-select wrappers read `picker.projects`).
   */
  renderProject: (project: ProjectListItem, picker: ProjectPicker) => ReactNode;
  /**
   * Called when the popover open state changes, AFTER the internal picker has
   * handled it. Receives the live picker so a consumer can run open-time side
   * effects (e.g. mint-on-open) with access to `projects` / `setOpen`.
   */
  onOpenChange?: (isOpen: boolean, picker: ProjectPicker) => void;
  /** Extra loading condition OR'd into the content's loading state. */
  additionalLoadingCheck?: boolean;
  /** Header label above the search input. Defaults to the search component's default. */
  searchTitle?: string;
  /**
   * Notifies the consumer whenever the loaded project list changes (pagination
   * / search). Lets a consumer run list-dependent effects (e.g. computing which
   * projects an artifact is attached to) without owning the picker itself.
   */
  onProjectsChange?: (projects: ProjectListItem[]) => void;
  /** Populates `permissions` on each project so rows can be gated. */
  includePermissions?: boolean;
  /**
   * Controls the popover's open state. When provided, the consumer owns open
   * state (e.g. to open the picker programmatically from another flow); when
   * omitted, the picker keeps owning it via its own internal state.
   */
  open?: boolean;
}

/**
 * Shared project-picker popover shell: owns `useProjectPicker` plus the
 * `DropdownMenu` + search + infinite-scroll content scaffolding. Consumers
 * supply only the row rendering (`renderProject`) and any open-time side effect
 * (`onOpenChange`); both `SaveToProjectPopover` and `StartBriefPopover` are thin
 * wrappers over this, so the picker shell evolves in one place.
 */
export function ProjectPickerPopover({
  children,
  workspaceId,
  renderProject,
  onOpenChange,
  additionalLoadingCheck = false,
  searchTitle,
  onProjectsChange,
  includePermissions = false,
  open,
}: ProjectPickerPopoverProps) {
  const picker = useProjectPicker(workspaceId, includePermissions, open);

  // Surface list changes to consumers that run list-dependent effects.
  useEffect(() => {
    onProjectsChange?.(picker.projects);
  }, [picker.projects, onProjectsChange]);

  const handleOpenChange = (isOpen: boolean) => {
    picker.handleOpenChange(isOpen);
    onOpenChange?.(isOpen, picker);
  };

  return (
    <DropdownMenu open={picker.open} onOpenChange={handleOpenChange}>
      <DropdownMenuTrigger asChild>{children}</DropdownMenuTrigger>
      <DropdownMenuContent className="w-60" align="center">
        <div className="flex flex-col max-h-96">
          <ProjectPickerSearch
            searchQuery={picker.searchQuery}
            onSearchChange={picker.setSearchQuery}
            title={searchTitle}
          />
          <ProjectPickerContent
            projects={picker.projects}
            isLoading={picker.isLoading}
            isFetchingNextPage={picker.isFetchingNextPage}
            searchQuery={picker.searchQuery}
            sentinelRef={picker.sentinelRef}
            additionalLoadingCheck={additionalLoadingCheck}
            renderProject={(project) => renderProject(project, picker)}
          />
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
